import { useEffect, useState } from 'react';
import { formatStreamDebugOverlayLine, type InputPhotonSummary } from '@playcastdotio/PeerCore';
import { guestInputPhotonSummary } from '../../../api/modules/peer';
import { useGuestStreamDebugSnapshot } from '../StreamDebugOverlay';

const PHOTON_POLL_MS = 1000;

export type StreamDebugSectionProps = {
  /** Test seams; production reads the live peer. */
  getRtcPeerConnection?: () => RTCPeerConnection | null;
  getInputPhotonSummary?: () => InputPhotonSummary | undefined;
};

const ms = (v: number | null | undefined): string => (typeof v === 'number' ? `${Math.round(v)}ms` : '—');

/**
 * The stream at a glance, inside the debug panel: the decode line that used to float on
 * its own over the top-left of the video, and the input-to-photon measurement with its
 * per-hop chain counts - the numbers the Android latency investigation is reading.
 */
export function StreamDebugSection({ getRtcPeerConnection, getInputPhotonSummary = guestInputPhotonSummary }: StreamDebugSectionProps) {
  const snapshot = useGuestStreamDebugSnapshot(getRtcPeerConnection);
  const [photon, setPhoton] = useState<InputPhotonSummary | undefined>(() => getInputPhotonSummary());

  useEffect(() => {
    const interval = setInterval(() => setPhoton(getInputPhotonSummary()), PHOTON_POLL_MS);
    return () => clearInterval(interval);
  }, [getInputPhotonSummary]);

  return (
    <div className='space-y-3'>
      <div>
        <div className='mb-1 font-semibold text-emerald-400'>Decode</div>
        <div data-testid='stream-debug-line' className='text-emerald-300'>
          {formatStreamDebugOverlayLine(snapshot)}
        </div>
      </div>
      <div>
        <div className='mb-1 font-semibold text-sky-400'>Input → photon</div>
        {photon ? (
          <div data-testid='input-photon-summary' className='space-y-0.5 text-sky-200'>
            <div>
              last {ms(photon.lastMs)} · p50 {ms(photon.p50Ms)} · p95 {ms(photon.p95Ms)} · n={photon.sampleCount}
            </div>
            <div className='text-sky-400/80'>
              chain: sampled {photon.chain.sampled} · tags {photon.chain.tagsAccepted}/{photon.chain.tagsAccepted + photon.chain.tagsIgnored} · frames {photon.chain.framesSeen} · implausible {photon.chain.implausible}
            </div>
          </div>
        ) : (
          <div className='text-zinc-500'>no session</div>
        )}
      </div>
    </div>
  );
}
